import React, { useEffect } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCIcon, RCCTextArea, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import cardStyle from "src/components/OrgChart/components/Milestone/styles";
import SortableContainer from "src/components/Sortable/Container";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { IComponentPremisses } from "./interface";

const Premisses: React.FC<IComponentPremisses> = ({
	control,
	error,
	premisesDefaultValue,
	disabled,
	register,
	unregister,
	clearErrors
}) => {
	const { translate } = useTranslation();

	const { 
		fields: fieldsPremise, 
		remove: removePremise,
		append: appendPremise,
		move: movePremise
	} = useFieldArray({
		control,
		name: "prtba008",
		shouldUnregister: true
	});

	const addPremise = () => {
		clearErrors("prtba008");
		const premise: any = {};
		premise.id = disabled ? "disabled" : v4();
		premise.key = premise.id;
		premise.pra008Id = "0";
		premise.pra008Premissa = "";

		appendPremise(premise);
	};

	const handleRemovePremise = (index: number) => {			
		removePremise(index);
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = fieldsPremise.findIndex((checks) => checks.id === activeId);
		const overIndex = fieldsPremise.findIndex((checks) => checks.id === overId);
		movePremise(activeIndex, overIndex);
		unregister("prtba008");
	};

	useEffect(() => {
		appendPremise(premisesDefaultValue.map((premise) => ({
			...premise,
			id: disabled ? "disabled" : v4()
		})));
	}, []);

	return (
		<RCCGrid
			container
			item
			direction="column"
			alignItems="center"
			justifyContent="center"
		>
			<RCCGrid
				container
				item
				xs={12}
				sm={12}
				md={8}
				lg={8}
			>
				<RCCGrid
					container
					item
				>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
					>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar Premissa")}
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								layout="isRounded"
								size="md"
								onClick={addPremise}
								disabled={disabled}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
								
				<RCCGrid
					container
					item
				>
					{error.prtba008 && error.prtba008.message && (
						<Title color="red" size="xs">
							<RCCIcon
								name="infoCircle"
								color="var(--red)"
								style={{ marginRight: "8px"}}
							/>
							{error.prtba008.message}
						</Title>
					)}
					{fieldsPremise.length === 0 && (
						<RCCAlert
							color="grey"
							title={translate("Nenhuma premissa adicionada")}
							description={translate("Aperte no botão acima para adicionar")}
						/>
					)}
					<SortableContainer 
						items={fieldsPremise}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToVerticalAxis]}
					>	
						{fieldsPremise.map((premise: any, index) => (
							<SortableItem id={premise.id} key={premise.key ? premise.key : `premise-${premise.pra008Id}`}>
								<RCCGrid
									container
									item
									direction="column"
									styleInline={cardStyle}
								>
									<Title color="purple" size="md">
										{index + 1}. {translate("Premissa")}
										{error.prtba008 && error.prtba008[index].message && (
											<span style={{ display: "flex", alignItems: "center", color: "var(--red)", fontSize: "0.8rem", marginLeft: "4px"}}>
												<RCCIcon
													name="infoCircle"
													color="var(--red)"
													style={{ marginRight: "8px"}}
												/>
												{error.prtba008[index].message}
											</span>
										)}
									</Title>
									<RCCGrid
										container
										item
										alignItems="flex-end"
									>
										<input 
											type="text"
											value={premise.pra008Id}
											{...register(`prtba008[${index}].pra008Id`)} 
											style={{ display: "none" }} 
											readOnly
										/>
										<RCCGrid
											container
											item
											justifyContent="center"
											xs={12}
											sm={10}
											styleInline="textarea{ height: 100px }"
										>
											<RCCTextArea 
												register={register}
												name={`prtba008[${index}].pra008Premissa`}
												maxLength={4000}
												defaultValue={premise.pra008Premissa}
												error={error.prtba008 && error.prtba008[index]?.pra008Premissa}
												disabled={disabled}
											/>
										</RCCGrid>
										<RCCGrid
											container
											item
											justifyContent="flex-end"
											xs={12}
											sm={2}
										>
											<SortableHandle>
												<RCCTooltip
													color="grey"
													text={translate("Ordenar")}
												>
													<RCCButton
														color="transparent"
														icon={{
															name: "arrows",
															color: "var(--grey)"
														}}
														layout="isRounded"
														disabled={disabled}
														tabIndex={-1}
													/>
												</RCCTooltip>	
											</SortableHandle>
											<RCCTooltip
												color="red"
												text={translate("Excluir")}
											>
												<RCCButton
													color="transparent"
													icon={{
														name: "trash",
														color: "var(--red)"
													}}
													layout="isRounded"
													onClick={() => handleRemovePremise(index)}
													disabled={disabled}
													tabIndex={-1}
												/>
											</RCCTooltip>
										</RCCGrid>
									</RCCGrid>
								</RCCGrid>										
							</SortableItem>
						))}
					</SortableContainer>
				</RCCGrid>
			</RCCGrid>
		</RCCGrid>
	);
};

export default Premisses;
